// @ts-nocheck
import { useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import Card from '../../../../common/card';
import CardDescription from '../../../../common/card-description';
import CardTitle from '../../../../common/card-title';
import l from '../../../../../lang';
import BriefcaseIcon from '../../../../../assets/icons/briefcase.svg';
import './styles.scss';
import ButtonCustom from '../../../../common/__deprecated__/button-custom';
import { Formik, FormikErrors, FormikValues } from 'formik';
import Input from '../../../../common/input';
import MessageError from '../../../../common/message-error';
import Select from '../../../../common/select';
import { accountTypesForSettlements } from '../../../../../utils/settlements-utils';
import { scrollIntoView } from '../../../../../utils';
import { getBankAccounts, getBanksForBankAccounts } from '../../../../../store/bank-accounts/actions';
import { Autocomplete } from '../../../../common/autocomplete';
import { hideFieldsByCountry, bankAccountNumberLabels, bankBranchLabels } from '../bank-account-config';

export type ValuesLevelTwoStepFields = {
  bankCode: string;
  bankName: string;
  accountType: string;
  bankAccount: string;
  bankAccountConfirm: string;
  bankBranch: string;
  beneficiaryAddress: string;
  beneficiaryCity: string;
};

const AddBankForm = ({
  loading,
  error,
  errorInfo,
  country,
  name,
  lastName,
  documentType,
  document,
  newBankAccount,
  getBanks,
  getBankAccounts,
  areBankAccountsAdded,
  FormObserver,
  handleSubmit,
  submitLabel,
  title,
  description,
}) => {
  const errorRef = useRef(null);

  useEffect(() => {
    getBankAccounts();
    getBanks(country);
  }, []);

  useEffect(() => {
    if (error) {
      scrollIntoView(errorRef);
    }
  }, [error]);

  const initialValues: ValuesLevelTwoStepFields = {
    bankCode: '',
    bankName: '',
    accountType: '',
    bankAccount: '',
    bankAccountConfirm: '',
    bankBranch: '',
    beneficiaryAddress: '',
    beneficiaryCity: '',
  };

  const validate = (values: FormikValues) => {
    const errors: FormikErrors<FormValues> = {};
    if (!hideFieldsByCountry[country]?.includes('bankCode') && !values.bankCode) {
      errors.bankCode = l('form.input.required');
    }
    if (!values.bankAccount) {
      errors.bankAccount = l('form.input.required');
    }
    if (!hideFieldsByCountry[country]?.includes('accountType')) {
      if (!values.accountType) {
        errors.accountType = l('form.input.required');
      }
    }
    if (!values.bankAccountConfirm) {
      errors.bankAccountConfirm = l('form.input.required');
    } else if (values.bankAccount !== values.bankAccountConfirm) {
      switch (country) {
        case 'ES':
          errors.bankAccountConfirm = l('beneficiary.notMatchIBAN');
          break;
        default:
          errors.bankAccountConfirm = l('beneficiary.notMatchBankAccount');
          break;
      }
    }
    if (!hideFieldsByCountry[country]?.includes('bankBranch') && !values.bankBranch) {
      errors.bankBranch = l('form.input.required');
    }
    if (!hideFieldsByCountry[country]?.includes('beneficiaryAddress')) {
      if (!values.beneficiaryAddress) {
        errors.beneficiaryAddress = l('form.input.required');
      }
    }
    if (!hideFieldsByCountry[country]?.includes('beneficiaryCity')) {
      if (!values.beneficiaryCity) {
        errors.beneficiaryCity = l('form.input.required');
      }
    }

    return errors;
  };

  const getBankAccountLabel = () => {
    if (bankAccountNumberLabels[country]) {
      return bankAccountNumberLabels[country];
    }
    return l('beneficiary.bankAccount');
  };

  const getBankAccountConfirmLabel = () => {
    if (bankAccountNumberLabels[country]) {
      return l('beneficiary.confirmAccount', bankAccountNumberLabels[country]);
    }
    return l('beneficiary.confirmBankAccount');
  };

  const getBankBranchLabel = () => {
    if (bankBranchLabels[country]) {
      return bankBranchLabels[country];
    }
    return l('beneficiary.branch');
  };

  const preHandleSubmit = values => {
    const valuesToSend = {};
    Object.keys(values).forEach(key => {
      if (typeof values[key] === 'string') {
        if (values[key].trim() !== '') {
          valuesToSend[key] = values[key];
        }
      } else {
        valuesToSend[key] = values[key];
      }
    });
    handleSubmit(valuesToSend);
  };

  const formLoading = loading || newBankAccount.loading;

  return (
    <div className="add_bank_form__section">
      <Card>
        {title && <CardTitle>{title}</CardTitle>}
        {description && <CardDescription>{description}</CardDescription>}
        <div className={`add_bank_form__note ${title || description ? 'add_bank_form__note_with_mt' : ''}`}>
          {l('companyInformation.kyc.companyForm.allDataRequired')}
        </div>
        <Formik initialValues={initialValues} validate={validate} onSubmit={values => preHandleSubmit(values)}>
          {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue }) => {
            return (
              <form onSubmit={handleSubmit}>
                {FormObserver}
                <div className="add_bank_form__title">
                  <img src={BriefcaseIcon} alt="user" />
                  <span>{l('companyInformation.kycTwo.companyOwnerLegalInformation')}</span>
                </div>
                <Input
                  name="beneficiaryName"
                  label={l('beneficiary.name')}
                  value={`${name || ''} ${lastName || ''}`.trim()}
                  disabled
                />
                <Input
                  name="country"
                  label={l('beneficiary.country')}
                  value={country ? l(`countryCode.${country}`) : ''}
                  disabled
                />
                <div className="add_bank_form__half_left">
                  <Input
                    name="beneficiaryDocumentType"
                    label={l('beneficiary.documentType')}
                    value={documentType || ''}
                    disabled
                  />
                </div>
                <div className="add_bank_form__half_right">
                  <Input name="beneficiaryDocument" label={l('beneficiary.document')} value={document || ''} disabled />
                </div>
                {
                  <>
                    <div className="add_bank_form__title">
                      <img src={BriefcaseIcon} alt="user" />
                      <span>{l('companyInformation.kycTwo.bankAccountInformation')}</span>
                    </div>
                    {!hideFieldsByCountry[country]?.includes('bankCode') && (
                      <Autocomplete
                        name="bankCode"
                        label={l('beneficiary.bankCode')}
                        value={values.bankCode ? `BANK_CODE_${values.bankCode}` : ''}
                        onBlur={handleBlur}
                        options={Object.keys(newBankAccount.banks || {}).map(key => ({
                          label: newBankAccount?.banks[key],
                          value: key,
                        }))}
                        onChange={e => {
                          const newValue = e.target.value;
                          if (newValue) {
                            const code = newValue.replace('BANK_CODE_', '');
                            setFieldValue('bankCode', code);
                            setFieldValue('bankName', newBankAccount.banks[newValue]);
                          } else {
                            setFieldValue('bankCode', '');
                            setFieldValue('bankName', '');
                          }
                        }}
                        disabled={_.isEmpty(newBankAccount.banks) || formLoading || newBankAccount.success}
                        error={touched.bankCode && errors.bankCode}
                        helperText={(touched.bankCode && errors.bankCode) || undefined}
                      />
                    )}
                    {!hideFieldsByCountry[country]?.includes('accountType') && (
                      <Select
                        name="accountType"
                        label={l('beneficiary.accountType')}
                        value={values.accountType}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        options={accountTypesForSettlements()}
                        disabled={formLoading || newBankAccount.success}
                        error={errors.accountType && touched.accountType && errors.accountType}
                      />
                    )}
                    <Input
                      name="bankAccount"
                      label={getBankAccountLabel()}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.bankAccount}
                      maxLength={50}
                      disabled={formLoading || newBankAccount.success}
                      error={errors.bankAccount && touched.bankAccount && errors.bankAccount}
                    />
                    <Input
                      name="bankAccountConfirm"
                      label={getBankAccountConfirmLabel()}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.bankAccountConfirm}
                      maxLength={50}
                      disabled={formLoading || newBankAccount.success}
                      error={errors.bankAccountConfirm && touched.bankAccountConfirm && errors.bankAccountConfirm}
                    />
                    {!hideFieldsByCountry[country]?.includes('bankBranch') && (
                      <Input
                        name="bankBranch"
                        label={getBankBranchLabel()}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.bankBranch}
                        maxLength={10}
                        disabled={formLoading || newBankAccount.success}
                        error={errors.bankBranch && touched.bankBranch && errors.bankBranch}
                      />
                    )}
                    {!hideFieldsByCountry[country]?.includes('beneficiaryAddress') && (
                      <Input
                        name="beneficiaryAddress"
                        label={l('beneficiary.address')}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.beneficiaryAddress}
                        maxLength={100}
                        disabled={formLoading || newBankAccount.success}
                        error={errors.beneficiaryAddress && touched.beneficiaryAddress && errors.beneficiaryAddress}
                      />
                    )}

                    {!hideFieldsByCountry[country]?.includes('beneficiaryCity') && (
                      <Input
                        name="beneficiaryCity"
                        label={l('beneficiary.city')}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.beneficiaryCity}
                        maxLength={100}
                        disabled={formLoading || newBankAccount.success}
                        error={errors.beneficiaryCity && touched.beneficiaryCity && errors.beneficiaryCity}
                      />
                    )}
                  </>
                }
                <div ref={errorRef}>
                  {newBankAccount.error && (
                    <div className="add_bank_form__message_error">
                      <MessageError errorInfo={newBankAccount.errorInfo} />
                    </div>
                  )}
                  {error && (
                    <div className="add_bank_form__message_error">
                      <MessageError errorInfo={errorInfo} />
                    </div>
                  )}
                </div>
                <button id="send-data-kyc-2" type="submit" hidden />
              </form>
            );
          }}
        </Formik>
        <div className="add_bank_form__send_btn">
          <ButtonCustom
            fullWidth
            text={submitLabel}
            onClick={() => window.document.getElementById('send-data-kyc-2')?.click()}
            loading={formLoading}
          />
        </div>
      </Card>
    </div>
  );
};

const mapStateToProps = state => ({
  loading: state.companyInformation.loading,
  error: state.companyInformation.error,
  errorInfo: state.companyInformation.errorInfo,
  country: state.dashboard.merchantMe?.country,
  name: state.dashboard.merchantMe?.name,
  lastName: state.dashboard.merchantMe?.lastName,
  documentType: state.dashboard.merchantMe?.documentType,
  document: state.dashboard.merchantMe?.document,
  isCompany: state.dashboard.merchantMe?.company,
  newBankAccount: state.bankAccounts.newBankAccount,
  areBankAccountsAdded: !!state.bankAccounts.allBankAccounts?.data?.length,
});

const mapDispatchToProps = dispatch => ({
  getBanks: country => dispatch(getBanksForBankAccounts(country)),
  getBankAccounts: () => dispatch(getBankAccounts()),
});

export default connect(mapStateToProps, mapDispatchToProps)(AddBankForm);
